package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.dsl.ir.model.overlay.OverlayDescriptor
import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.Edges
import top.wcpe.allincore.layout.engine.LayoutNode
import top.wcpe.allincore.layout.engine.LayoutStyle
import top.wcpe.allincore.layout.engine.PositionMode
import top.wcpe.allincore.ui.runtime.event.CharEvent
import top.wcpe.allincore.ui.runtime.event.DragEvent
import top.wcpe.allincore.ui.runtime.event.DragOverEvent
import top.wcpe.allincore.ui.runtime.event.DragStartEvent
import top.wcpe.allincore.ui.runtime.event.DropEvent
import top.wcpe.allincore.ui.runtime.event.EventTarget
import top.wcpe.allincore.ui.runtime.event.FocusEvent
import top.wcpe.allincore.ui.runtime.event.KeyEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.ScrollEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.internal.collections.readOnlyCopy
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContextHandle
import top.wcpe.allincore.ui.runtime.lifecycle.LifecycleAware
import top.wcpe.allincore.ui.runtime.lifecycle.LifecycleScope
import top.wcpe.allincore.ui.runtime.reactive.EffectBody
import top.wcpe.allincore.ui.runtime.style.Style
import kotlin.jvm.JvmStatic

/**
 * UI 元素树的抽象基类。
 *
 * 使用自类型 [SELF] 让流式 setter 在子类继续返回子类自身，避免链式调用退化。
 *
 * 同时实现 [LayoutNode]、[EventTarget]、[LifecycleAware]，
 * 可直接交给布局引擎、事件分发器与生命周期管理器。
 */
@Suppress("TooManyFunctions", "LargeClass", "MagicNumber")
abstract class UIElement<SELF : UIElement<SELF>> :
    LayoutNode,
    EventTarget,
    LifecycleAware {

    /** 元素自身样式。 */
    protected val styleField: Style = Style.create()

    /** 子元素列表，保持插入顺序。 */
    protected val childrenField: MutableList<UIElement<*>> = ArrayList()

    /** 父元素引用（FR-30 B1 push 式脏标记）：供布局脏位沿树上传到根；由 [child] / [replaceChildren] 维护，root 为 null。 */
    private var layoutParentField: UIElement<*>? = null

    /** 布局脏位（FR-30 B1）：初始为脏（首帧必算）；影响布局的 mutation 经 [markLayoutDirty] 置脏并沿 parent 上传。 */
    private var layoutDirtyField: Boolean = true

    /**
     * 渲染脏位（FR-30 B4）：初始为脏（首帧必绘）；影响渲染的 mutation（含纯视觉，比布局脏更宽：颜色 / opacity /
     * hover / 动画等）经 [markRenderDirty] 置脏并沿 parent 上传。供子树级渲染缓存判定「子树静态（renderDirty=false）→复用上帧绘制命令」。
     */
    private var renderDirtyField: Boolean = true

    /** 生命周期作用域，元素 dispose 时统一释放挂在其上的 Effect/订阅。 */
    protected val lifecycleScopeField: LifecycleScope = LifecycleScope()

    /** 可选 ID，便于查询/调试。 */
    protected var idField: String? = null

    /** IR 建树时注入的稳定运行时节点路径；手工树保持 null，由采集器生成 DFS 路径。 */
    private var runtimeNodePathField: String? = null

    /** 可选 tooltip 文本，鼠标悬停时由渲染器顶层绘制。 */
    protected var tooltipField: String = ""

    /**
     * HUD 锚点（Stage 14）：HUD 场景下 root 元素声明此属性，让平台层按
     * (屏幕尺寸 - 元素 layout 尺寸) 计算偏移，自动贴边/居中，DSL 不必硬编码坐标。
     *
     * 合法值（解析由平台层 HudAnchorParser 完成，非法值降级为左上角）：
     * top-left / top-center / top-right /
     * center-left / center / center-right /
     * bottom-left / bottom-center / bottom-right。
     *
     * null / 空串：不应用锚点（默认左上角，与无 anchor 行为一致）。
     *
     * 仅 fabric HudRenderHook 在 root 节点上消费此属性；非 HUD 场景
     * （如 Screen / 嵌套子元素）此属性不参与布局，纯元数据。
     */
    protected var hudAnchorField: String? = null

    /**
     * 是否绘制原版 Screen 背景。仅根元素在 Screen 场景下生效；默认开启以保持现有页面行为。
     * 关闭后不会绘制世界上方的黑色半透明遮罩。
     */
    protected var screenBackgroundField: Boolean = true

    /**
     * OS 鼠标光标形状（净新增的悬停驱动元数据）：null / 空串=未声明（默认箭头）。
     *
     * 合法值（解析由 CursorType.fromToken 完成，非法值降级为 DEFAULT）：
     * default / pointer / text / move / resize-h / resize-v /
     * resize-nwse / resize-nesw / not-allowed / wait。
     *
     * 由主树 hover 命中链（InputDispatcher.dispatchMouseMove）消费：UIRuntimeScreen
     * 与 HUD 交互屏共用同一 dispatch，故单个 HUD 上 cursor 同样生效。已知裁剪：
     * ① dropdown/overlay 浮层内容元素上的 cursor 不被消费（浮层走独立命中链）；
     * ② 多个 HUD 会话同帧并存时按「最后一个会话」覆盖，可能闪烁。纯客户端渲染期
     * 元数据，不进任何序列化/协议/IR wire 格式（类比 hudAnchor）。
     */
    protected var cursorField: String? = null

    /** 已解析的动画触发引用。 */
    protected val animationRefs: MutableMap<String, UiAnimation> = LinkedHashMap()

    /** 当前元素挂接的 overlay 描述符（由 IR 的 elementOverlays 注入）。 */
    protected val overlayDescriptors: MutableList<OverlayDescriptor> = ArrayList()

    /** 当前元素挂接的调度器（中立句柄，marker 断环，见 [AnimationDriverHandle]）；由 RenderRoot 管理。 */
    protected var animationSchedulerField: AnimationDriverHandle? = null

    /** runtime 渲染态：平移、缩放、旋转、倾斜、透视与透明度。 */
    protected val transform: RenderTransform = RenderTransform()

    /** 设置字体样式（任意元素均可；null=继承上层）。 */
    fun font(font: FontStyle?): SELF {
        this.fontField = font
        return self()
    }

    /** 当前元素声明的字体样式；未声明返回 null。 */
    fun font(): FontStyle? = fontField

    /**
     * 字体样式（namespaced 名 + 三态 shadow/bold/italic，null=继承上层）。
     *
     * 放在基类让**任意带文字控件**（text/button/textfield/dropdown/...）都能配字体；
     * 渲染时 [top.wcpe.allincore.ui.runtime.theme.FontAccess] 由 UIRenderer 递归压成「元素层」，
     * 与「页面 / 全局」层合并为最终字体，并对子孙级联。null 表示该元素不声明字体（完全继承）。
     */
    protected var fontField: FontStyle? = null

    /**
     * 渲染层级偏移。默认 0 = 与普通元素同层。
     *
     * 正数值让元素在父容器的渲染顺序中更靠上（盖住 zIndex 更低的兄弟元素）。
     * 典型用途：Slot 子元素设 zIndex > 0 即可在物品图标之上渲染。
     * 通知元素设 zIndex = 200（高于 vanilla items 的 z=150）。
     */
    protected var zIndexField: Int = 0

    /** 获取渲染变换对象。 */
    fun transform(): RenderTransform = transform

    protected var pointerEventsField: Boolean = true

    /** 标记 pointerEvents 是否被用户显式设置过。 */
    private var pointerEventsExplicit: Boolean = false

    /**
     * M6-f §8.2：是否可见。false 时不参与布局（[LayoutNode.visible]）、
     * 不渲染、不响应事件（命中测试在 pointerEvents 之外再短路一次）。
     */
    protected var visibleField: Boolean = true

    /**
     * 是否为「透明布局容器」（CSS `display: contents` 语义，见 [LayoutNode.transparentLayout]）。
     *
     * $for/$cond 的透明包裹 box 置 true：在 flex 父下布局引擎会摊平它、把循环/分支项直接挂到父，
     * 使父的 gap/align/justify/wrap 真正作用于这些子项。仅影响布局摊平，不改变渲染/事件树结构。
     */
    protected var layoutTransparent: Boolean = false

    /**
     * 是否渲染在物品图标之上。
     *
     * vanilla `renderItem` 在 GUI z≈150 绘制物品并写入深度，普通 z≈0 的 UI 内容会被遮挡。
     * `OVERLAY` 层经 `LayerBufferingBridge` 整体抬高 z 到物品之上；置 true 时
     * UIRenderer 会把本元素改投到 `OVERLAY` 层渲染，从而把数量/标签等叠加文本提到物品上层。
     * 默认 false 与历史行为一致。
     */
    protected var aboveItemField: Boolean = false

    /**
     * 计划 07 M5：是否参与布局动画（FLIP）。
     *
     * 标记式 opt-in：仅置 true 的元素在 Taffy 整树重算导致绝对位置变化时，由
     * LayoutAnimator 捕获重算前盒（first）/
     * 重算后盒（last），自动以反向 `translateX/translateY` 将其"钉"在旧位置，
     * 再用弹簧/补间把 translate 收敛到 0，形成"从旧位置平滑滑到新位置"的视觉效果。
     * 默认 false 与历史行为一致（不捕获、不补间，零回归）。该标记由 IR 元素节点的
     * animateLayout 标记位经 IrInterpreter 接线注入，不是运行时 prop。
     */
    protected var animateLayoutField: Boolean = false

    /**
     * Phase 3 StickyHeader：标记该子元素在滚动容器内"吸顶"。
     *
     * true 时,当滚动使其滚出 viewport 顶部,渲染端把它固定在 viewport 顶部 + [stickyOffset]。
     * 仅在 [ScrollableContainer] 的直接子节点上有意义。
     */
    protected var stickyField: Boolean = false

    /** 吸顶时距 viewport 顶部的偏移(像素)。 */
    protected var stickyOffsetField: Float = 0f

    /**
     * 溢出处理方式(对齐 CSS overflow 心智模型)。
     *
     * [Overflow.VISIBLE](默认):子内容可绘制到自身包围盒之外,不裁剪——
     * 与历史行为一致,保证向后兼容。[Overflow.HIDDEN]:渲染端把子树裁剪到
     * 自身包围盒内,溢出部分不可见。
     *
     * 典型用途:缩放视口(`Zoomable`)、固定尺寸内容框等"视窗"语义容器,
     * 内容放大/平移后需裁剪在框内。普通装饰层(如下拉刷新指示器)保持 VISIBLE
     * 以允许刻意溢出。
     */
    protected var overflowField: Overflow = Overflow.VISIBLE

    /**
     * RFC-INPUT-EVENT-UNIFICATION Stage 3：通用 hotkey 绑定字符串。
     *
     * 键盘组合（如 `"Enter"` / `"Ctrl+S"`）或鼠标侧键（`"MouseBack"`
     * / `"Ctrl+MouseForward"`）。null 表示未绑定。匹配大小写不敏感，由
     * [findHotkeyMatch] 统一处理。
     */
    protected var hotkeyField: String? = null

    /** 鼠标点击 handler，可空。 */
    protected var onClickField: UiEventHandler<MouseEvent>? = null

    /** 鼠标右键点击 handler，可空。 */
    protected var onRightClick: UiEventHandler<MouseEvent>? = null

    /** 鼠标悬停 handler，可空。 */
    protected var onHover: UiEventHandler<MouseEvent>? = null

    /** 长按 handler，可空。 */
    protected var onLongPress: UiEventHandler<MouseEvent>? = null

    /** 拖拽进入 handler，可空。 */
    protected var onDragEnter: UiEventHandler<MouseEvent>? = null

    /** 拖拽离开 handler，可空。 */
    protected var onDragLeave: UiEventHandler<MouseEvent>? = null

    /** Phase 4 全局拖拽:开始拖拽 handler(源元素),可空。 */
    protected var onDragStart: UiEventHandler<DragStartEvent>? = null

    /** Phase 4 全局拖拽:拖拽悬停 handler(放置目标),可空。 */
    protected var onDragOver: UiEventHandler<DragOverEvent>? = null

    /** Phase 4 全局拖拽:放下 handler(放置目标),可空。 */
    protected var onDrop: UiEventHandler<DropEvent>? = null

    /** Phase 4 全局拖拽:是否为可放置目标。 */
    protected var dropTargetField: Boolean = false

    /** 通用值变化 handler（非控件专用），可空。子类控件有各自的 typed onChange。 */
    protected var onChangeGeneric: UiEventHandler<MouseEvent>? = null

    /** 键盘事件 handler，可空。 */
    protected var onKeyField: UiEventHandler<KeyEvent>? = null

    /** 字符输入 handler，可空。 */
    protected var onCharField: UiEventHandler<CharEvent>? = null

    /** 鼠标拖拽 handler，可空。 */
    protected var onDragField: UiEventHandler<DragEvent>? = null

    /** v4 C.2：拖拽结束 handler（鼠标释放或捕获被解除时触发），可空。 */
    protected var onDragEndField: UiEventHandler<DragEvent>? = null

    /** v4 C.2：指针移动 handler（每次鼠标 move 都会触发，比 onHover 更高频），可空。 */
    protected var onPointerMove: UiEventHandler<MouseEvent>? = null

    /** 鼠标滚轮 handler，可空。 */
    protected var onScrollField: UiEventHandler<ScrollEvent>? = null

    /** 获得焦点 handler，可空。 */
    protected var onFocusGained: UiEventHandler<FocusEvent>? = null

    /** 失去焦点 handler，可空。 */
    protected var onFocusLost: UiEventHandler<FocusEvent>? = null

    /** 是否可获取焦点；交互控件子类构造时置 true。 */
    protected var focusableField: Boolean = false

    /** 阶段 13：tabIndex 显式覆盖 Tab 顺序；[TAB_INDEX_UNSET] 表示沿用 DOM 声明顺序兜底。 */
    protected var tabIndexField: Int = TAB_INDEX_UNSET

    /**
     * v4 A.1：contextMenu 长按触发阈值（毫秒）；
     * [CONTEXT_MENU_TRIGGER_MS_UNSET] 表示未声明，平台层回退到主题 longPressThresholdMs。
     * 作用域：任意可声明 contextMenu 的元素。
     */
    protected var contextMenuTriggerMsField: Long = CONTEXT_MENU_TRIGGER_MS_UNSET

    /** 视觉状态：鼠标是否悬停在元素上。 */
    protected var hoveredField: Boolean = false

    /** 视觉状态：鼠标按下未释放。 */
    protected var pressedField: Boolean = false

    /** 视觉状态：当前是否持有焦点。 */
    protected var focusedField: Boolean = false

    /** 鼠标进入元素的时间戳（毫秒），未悬停时为 [HOVER_SINCE_MS_UNSET]。 */
    protected var hoverSinceMsField: Long = 0L

    /** 子类返回 `this`，用于自类型递归。 */
    protected abstract fun self(): SELF

    open fun id(id: String?): SELF {
        this.idField = id
        return self()
    }

    /** 获取渲染层级偏移。 */
    fun zIndex(): Int = zIndexField

    /** 设置渲染层级偏移；正数值让元素盖住 zIndex 更低的兄弟元素。 */
    @Suppress("UNCHECKED_CAST")
    fun zIndex(zIndex: Int): SELF {
        this.zIndexField = zIndex
        return this as SELF
    }

    open fun child(child: UIElement<*>): SELF {
        child.layoutParentField = this
        this.childrenField.add(child)
        markLayoutDirty() // FR-30 B1：子结构变化影响布局
        return self()
    }

    /**
     * 内部 API（M6-c 数据驱动容器）：按给定顺序替换 children 引用列表。
     *
     * 仅交换列表中的引用，不会调用 detach/attach；保留每个 child 的 runtime
     * 状态（hover/focus/animation 等）。普通使用者不应调用此方法。
     */
    fun replaceChildren(newChildren: List<UIElement<*>>?): SELF {
        // FR-30 B1：维护 parent 一致性——旧子脱离、新子接上（不调生命周期，符合"仅换引用"语义）。
        this.childrenField.forEach { it.layoutParentField = null }
        this.childrenField.clear()
        if (newChildren != null) {
            newChildren.forEach { it.layoutParentField = this }
            this.childrenField.addAll(newChildren)
        }
        markLayoutDirty() // FR-30 B1：子结构变化影响布局
        return self()
    }

    /** 本元素布局是否脏（需重算布局）。FR-30 B1。 */
    fun isLayoutDirty(): Boolean = layoutDirtyField

    /** 父元素引用（布局脏标记上传用）；root 为 null。FR-30 B1。 */
    fun layoutParent(): UIElement<*>? = layoutParentField

    /**
     * 标记本元素布局脏并沿 parent 链上传到根（FR-30 B1 push 式脏标记）。
     *
     * 不变量：脏节点的所有祖先必为脏——故遇到已脏节点即可短路停传（其祖先已脏）。
     * 影响布局的 mutation（样式 / 文字 / 可见性 / 子结构 / 主题）应调用本方法。
     */
    fun markLayoutDirty() {
        markRenderDirty() // FR-30 B4：布局变化必然影响渲染，连带标渲染脏
        var node: UIElement<*>? = this
        while (node != null && !node.layoutDirtyField) {
            node.layoutDirtyField = true
            node = node.layoutParentField
        }
    }

    /** 清除本元素布局脏位（布局求解后调用）。FR-30 B1。 */
    fun clearLayoutDirty() {
        layoutDirtyField = false
    }

    /**
     * 递归清除本元素及整棵子树的布局脏位（布局求解后调用）。FR-30 B1。
     * 维护「脏节点祖先必脏」不变量——整树全清即无脏节点，下次任意 mutation 标脏可正常沿 parent 上传。
     */
    fun clearLayoutDirtyTree() {
        layoutDirtyField = false
        for (child in childrenField) {
            child.clearLayoutDirtyTree()
        }
    }

    /** 本元素渲染是否脏（自身或某后代有渲染变化）。FR-30 B4。 */
    fun isRenderDirty(): Boolean = renderDirtyField

    /**
     * 标记本元素渲染脏并沿 parent 链上传到根（FR-30 B4 push 式渲染脏标记）。
     *
     * 不变量与 [markLayoutDirty] 同：脏节点的所有祖先必为脏——故遇到已脏节点即可短路停传。
     * 影响渲染的 mutation（任意样式 / 文字 / 可见性 / 子结构 / 动画 / 交互态 / 主题）应调用本方法。
     */
    fun markRenderDirty() {
        var node: UIElement<*>? = this
        while (node != null && !node.renderDirtyField) {
            node.renderDirtyField = true
            node = node.layoutParentField
        }
    }

    /** 清除本元素渲染脏位（绘制 / 缓存录制后调用）。FR-30 B4。 */
    fun clearRenderDirty() {
        renderDirtyField = false
    }

    /**
     * 递归清除本元素及整棵子树的渲染脏位（绘制后调用）。FR-30 B4。
     * 维护「脏节点祖先必脏」不变量——整树全清即无脏节点，下次任意 mutation 标脏可正常沿 parent 上传。
     */
    fun clearRenderDirtyTree() {
        renderDirtyField = false
        for (child in childrenField) {
            child.clearRenderDirtyTree()
        }
    }

    /** 用 `other` 的字段值覆盖当前 style（浅拷贝字段）。 */
    fun style(other: Style): SELF {
        this.styleField.copyFrom(other)
        return self()
    }

    fun style(): Style = styleField

    fun id(): String? = idField

    /** 返回 IR 注入的稳定运行时节点路径；手工元素返回 null。 */
    internal fun runtimeNodePath(): String? = runtimeNodePathField

    /** 仅供 IR 建树入口写入稳定运行时节点路径。 */
    internal fun assignRuntimeNodePath(nodePath: String) {
        runtimeNodePathField = nodePath
    }

    open fun tooltip(tooltip: String?): SELF {
        this.tooltipField = tooltip ?: ""
        return self()
    }

    open fun tooltip(): String = tooltipField

    /** 设置 HUD 锚点；null 或空串视为未声明（默认左上角）。 */
    fun hudAnchor(anchor: String?): SELF {
        this.hudAnchorField = if (anchor.isNullOrEmpty()) null else anchor
        return self()
    }

    /** 获取 HUD 锚点；未声明返回 null。 */
    fun hudAnchor(): String? = hudAnchorField

    /** 设置是否绘制原版 Screen 背景。 */
    fun screenBackground(enabled: Boolean): SELF {
        this.screenBackgroundField = enabled
        return self()
    }

    /** 是否绘制原版 Screen 背景，默认开启。 */
    fun screenBackground(): Boolean = screenBackgroundField

    /**
     * 设置 OS 鼠标光标形状（如 `"pointer"`）；null 或空串视为未声明（默认箭头）。
     *
     * 纯客户端悬停期元数据：由 InputDispatcher 在 hover 命中链末端解析为 CursorType
     * 经 CursorBridge 上报平台层。未声明时不触发任何 setCursor，行为与历史一致。
     */
    fun cursor(cursor: String?): SELF {
        this.cursorField = if (cursor.isNullOrEmpty()) null else cursor
        return self()
    }

    /** 获取光标形状字符串；未声明返回 null。 */
    fun cursor(): String? = cursorField

    fun animationRef(trigger: String?, animation: UiAnimation?): SELF {
        if (trigger != null && animation != null) {
            animationRefs[trigger] = animation
        }
        return self()
    }

    fun animationFor(trigger: String?): UiAnimation? = animationRefs[trigger]

    /** 动画触发引用表；只读拷贝（readOnlyCopy 契约），与迁移前 Collections.unmodifiableMap 行为等价。 */
    fun animationRefs(): Map<String, UiAnimation> = readOnlyCopy(animationRefs)

    /** 挂接一个 overlay 描述符。 */
    fun overlayDescriptor(descriptor: OverlayDescriptor?): SELF {
        if (descriptor != null) {
            overlayDescriptors.add(descriptor)
        }
        return self()
    }

    /** 读取当前元素挂接的 overlay 描述符。 */
    /** 读取当前元素挂接的 overlay 描述符；只读拷贝（readOnlyCopy 契约），与迁移前 Collections.unmodifiableList 行为等价。 */
    fun overlayDescriptors(): List<OverlayDescriptor> = readOnlyCopy(overlayDescriptors)

    /**
     * 数据驱动(for/source)实例化时捕获的 per-cell EvalContext(含循环形参 cell 等 locals；common 面持 [EvalContextHandle] 中立句柄)。
     *
     * 静态元素为 null；overlay 渲染时若非 null,优先用它而非主屏全局 ctx,
     * 使循环槽 tooltip 内的 `cell.X` 能解析到当前格(见 OverlayLayer.renderHoverTooltip)。
     */
    private var capturedEvalContextField: EvalContextHandle? = null

    /** 设置 per-cell EvalContext(仅数据驱动实例化路径调用；中立句柄，marker 断环，见 [EvalContextHandle])。 */
    fun capturedEvalContext(context: EvalContextHandle?) {
        this.capturedEvalContextField = context
    }

    /** 读取 per-cell EvalContext;静态元素返回 null。 */
    fun capturedEvalContext(): EvalContextHandle? = capturedEvalContextField

    /**
     * M6-i：触发器名 → onComplete 回调（common 无 java.lang.Runnable，改用既有 SAM [EffectBody]）；
     * 由 IR 解释器在装配 `<trigger>Complete` PropAssign 时注入，scheduler.play 返回 handle 后
     * 通过此 map 绑定到 AnimationHandle.onComplete。元素 dispose 时回调随 LifecycleScope 一并失效。
     */
    protected val animationCompleteCallbacks: MutableMap<String, EffectBody> = LinkedHashMap()

    /** M6-i：注册某触发器对应的 onComplete 回调，覆盖既有同名回调。 */
    fun animationCompleteCallback(trigger: String?, callback: EffectBody?): SELF {
        if (trigger != null && callback != null) {
            animationCompleteCallbacks[trigger] = callback
        }
        return self()
    }

    /** M6-i：取出某触发器对应的 onComplete 回调；不存在返回 null。 */
    fun animationCompleteCallbackFor(trigger: String?): EffectBody? = animationCompleteCallbacks[trigger]

    fun attachAnimationScheduler(scheduler: AnimationDriverHandle?) {
        this.animationSchedulerField = scheduler
    }

    fun animationScheduler(): AnimationDriverHandle? = animationSchedulerField

    fun getAnimationScheduler(): AnimationDriverHandle? = animationSchedulerField

    fun translateX(value: Float): SELF {
        transform.setTranslateX(value)
        return self()
    }

    fun translateY(value: Float): SELF {
        transform.setTranslateY(value)
        return self()
    }

    fun scaleX(value: Float): SELF {
        transform.setScaleX(value)
        return self()
    }

    fun scaleY(value: Float): SELF {
        transform.setScaleY(value)
        return self()
    }

    fun opacity(value: Float): SELF {
        transform.setOpacity(value)
        return self()
    }

    fun translateX(): Float = transform.getTranslateX()

    fun translateY(): Float = transform.getTranslateY()

    fun scaleX(): Float = transform.getScaleX()

    fun scaleY(): Float = transform.getScaleY()

    fun opacity(): Float = transform.getOpacity()

    fun rotation(value: Float): SELF {
        transform.setRotation(value)
        return self()
    }

    fun rotation(): Float = transform.getRotation()

    /** 绕 X 轴旋转（弧度），用于 3D 翻牌"上下翻"效果。需 GPU 路径透视矩阵生效。 */
    fun rotateX(value: Float): SELF {
        transform.setRotateX(value)
        return self()
    }

    fun rotateX(): Float = transform.getRotateX()

    /** 绕 Y 轴旋转（弧度），用于 3D 翻牌"左右翻"效果。需 GPU 路径透视矩阵生效。 */
    fun rotateY(value: Float): SELF {
        transform.setRotateY(value)
        return self()
    }

    fun rotateY(): Float = transform.getRotateY()

    fun skewX(value: Float): SELF {
        transform.setSkewX(value)
        return self()
    }

    fun skewX(): Float = transform.getSkewX()

    fun skewY(value: Float): SELF {
        transform.setSkewY(value)
        return self()
    }

    fun skewY(): Float = transform.getSkewY()

    /**
     * 设置变换原点 X（归一化比例）：0=左边、0.5=盒中心、1=右边。
     *
     * 影响 rotation/rotateX/rotateY/skew/scale 的旋转/缩放中心；默认 0.5 等于历史盒中心行为。
     */
    fun originX(value: Float): SELF {
        transform.setOriginX(value)
        return self()
    }

    fun originX(): Float = transform.getOriginX()

    /**
     * 设置变换原点 Y（归一化比例）：0=上边、0.5=盒中心、1=下边。
     *
     * 影响 rotation/rotateX/rotateY/skew/scale 的旋转/缩放中心；默认 0.5 等于历史盒中心行为。
     */
    fun originY(value: Float): SELF {
        transform.setOriginY(value)
        return self()
    }

    fun originY(): Float = transform.getOriginY()

    /**
     * 设置透视距离。
     *
     * 若 perspective 被显式设置为非默认值且 pointerEvents 未被显式覆盖，
     * 自动将 pointerEvents 设为 false（RFC §11.2 决断）。
     */
    fun perspective(value: Float): SELF {
        transform.setPerspective(value)
        if (value != DEFAULT_PERSPECTIVE && !pointerEventsExplicit) {
            this.pointerEventsField = false
        }
        return self()
    }

    fun perspective(): Float = transform.getPerspective()

    fun pointerEvents(value: Boolean): SELF {
        this.pointerEventsField = value
        this.pointerEventsExplicit = true
        return self()
    }

    fun pointerEvents(): Boolean = pointerEventsField

    /** M6-f §8.2：设置元素可见性。 */
    fun visible(value: Boolean): SELF {
        this.visibleField = value
        markLayoutDirty() // FR-30 B1：可见性影响布局（visible=false 不参与布局）
        return self()
    }

    override fun visible(): Boolean = visibleField

    /** 标记本元素为透明布局容器（`display: contents`），见 [LayoutNode.transparentLayout]。 */
    fun layoutTransparent(value: Boolean): SELF {
        this.layoutTransparent = value
        return self()
    }

    override fun transparentLayout(): Boolean = layoutTransparent

    /** 设置是否渲染在物品图标之上；true 时本元素改投到 OVERLAY 层，提到 vanilla 物品 z 之上。 */
    fun aboveItem(value: Boolean): SELF {
        this.aboveItemField = value
        return self()
    }

    /** 是否渲染在物品图标之上。 */
    fun aboveItem(): Boolean = aboveItemField

    /** 计划 07 M5：标记本元素参与布局动画（FLIP）；true 时由 LayoutAnimator 捕获其位置变化并补间。 */
    fun animateLayout(value: Boolean): SELF {
        this.animateLayoutField = value
        return self()
    }

    /** 计划 07 M5：本元素是否参与布局动画（FLIP）。 */
    fun animateLayout(): Boolean = animateLayoutField

    /** Phase 3 StickyHeader：标记该元素在滚动容器内吸顶。 */
    fun sticky(value: Boolean): SELF {
        this.stickyField = value
        return self()
    }

    fun sticky(): Boolean = stickyField

    /** 吸顶偏移(距 viewport 顶部像素)。 */
    fun stickyOffset(value: Float): SELF {
        this.stickyOffsetField = value
        return self()
    }

    fun stickyOffset(): Float = stickyOffsetField

    /** 溢出处理方式枚举,语义对齐 CSS overflow。 */
    enum class Overflow {
        /** 不裁剪:子内容可溢出自身包围盒(默认,与历史行为一致)。 */
        VISIBLE,

        /** 裁剪:渲染端把子树裁剪到自身包围盒内。 */
        HIDDEN,
        ;

        companion object {
            /**
             * 解析字符串到枚举,大小写不敏感。`"clip"` 作为 `"hidden"` 的别名。
             * 无法识别时返回 [VISIBLE]。
             */
            @JvmStatic
            fun parse(s: String?): Overflow {
                if (s == null) return VISIBLE
                return when (s.trim().lowercase()) {
                    "hidden", "clip" -> HIDDEN
                    else -> VISIBLE
                }
            }
        }
    }

    /** 设置溢出处理方式。 */
    fun overflow(value: Overflow?): SELF {
        this.overflowField = value ?: Overflow.VISIBLE
        return self()
    }

    fun overflow(): Overflow = overflowField

    override fun children(): List<UIElement<*>> = childrenField

    override fun layoutStyle(): LayoutStyle = styleField.layout()

    // -------- 事件 setter / EventTarget 实现 --------

    open fun onClick(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onClickField = handler
        return self()
    }

    fun onRightClick(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onRightClick = handler
        return self()
    }

    fun onHover(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onHover = handler
        return self()
    }

    fun onLongPress(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onLongPress = handler
        return self()
    }

    fun onDragEnter(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onDragEnter = handler
        return self()
    }

    fun onDragLeave(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onDragLeave = handler
        return self()
    }

    // -------- Phase 4 全局拖拽 API --------

    /** 标记/查询是否为可放置目标。 */
    fun dropTarget(enabled: Boolean): SELF {
        this.dropTargetField = enabled
        return self()
    }

    fun dropTarget(): Boolean = dropTargetField

    fun onDragStart(handler: UiEventHandler<DragStartEvent>?): SELF {
        this.onDragStart = handler
        return self()
    }

    fun onDragStartHandler(): UiEventHandler<DragStartEvent>? = onDragStart

    fun onDragOver(handler: UiEventHandler<DragOverEvent>?): SELF {
        this.onDragOver = handler
        return self()
    }

    fun onDragOverHandler(): UiEventHandler<DragOverEvent>? = onDragOver

    fun onDrop(handler: UiEventHandler<DropEvent>?): SELF {
        this.onDrop = handler
        return self()
    }

    fun onDropHandler(): UiEventHandler<DropEvent>? = onDrop

    fun onChangeGeneric(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onChangeGeneric = handler
        return self()
    }

    open fun onKey(handler: UiEventHandler<KeyEvent>?): SELF {
        this.onKeyField = handler
        return self()
    }

    open fun onChar(handler: UiEventHandler<CharEvent>?): SELF {
        this.onCharField = handler
        return self()
    }

    open fun onDrag(handler: UiEventHandler<DragEvent>?): SELF {
        this.onDragField = handler
        return self()
    }

    /** v4 C.2：设置拖拽结束 handler。 */
    open fun onDragEnd(handler: UiEventHandler<DragEvent>?): SELF {
        this.onDragEndField = handler
        return self()
    }

    /** v4 C.2：设置指针移动 handler；每次鼠标 move 都触发。 */
    fun onPointerMove(handler: UiEventHandler<MouseEvent>?): SELF {
        this.onPointerMove = handler
        return self()
    }

    open fun onScroll(handler: UiEventHandler<ScrollEvent>?): SELF {
        this.onScrollField = handler
        return self()
    }

    fun onFocusGained(handler: UiEventHandler<FocusEvent>?): SELF {
        this.onFocusGained = handler
        return self()
    }

    fun onFocusLost(handler: UiEventHandler<FocusEvent>?): SELF {
        this.onFocusLost = handler
        return self()
    }

    fun focusable(v: Boolean): SELF {
        this.focusableField = v
        return self()
    }

    /** 阶段 13：设置 tabIndex；写入 [TAB_INDEX_UNSET] 等价于"未声明，按 DOM 顺序"。 */
    fun tabIndex(index: Int): SELF {
        this.tabIndexField = index
        return self()
    }

    /** 阶段 13：tabIndex 当前值；返回 [TAB_INDEX_UNSET] 表示未声明。 */
    fun tabIndex(): Int = tabIndexField

    /** v4 A.1：设置 contextMenuTriggerMs；写入 [CONTEXT_MENU_TRIGGER_MS_UNSET]（0L）等价于"未声明，回退主题"。 */
    fun contextMenuTriggerMs(ms: Long): SELF {
        this.contextMenuTriggerMsField = ms
        return self()
    }

    /** v4 A.1：contextMenuTriggerMs 当前值；返回 [CONTEXT_MENU_TRIGGER_MS_UNSET] 表示未声明。 */
    fun contextMenuTriggerMs(): Long = contextMenuTriggerMsField

    open fun hovered(v: Boolean): SELF {
        if (this.hoveredField != v) {
            this.hoverSinceMsField = if (v) currentTimeMillis() else HOVER_SINCE_MS_UNSET
            markRenderDirty() // FR-30 B4：hover 态变化影响视觉高亮（不经 PropAppliers）
        }
        this.hoveredField = v
        return self()
    }

    fun pressed(v: Boolean): SELF {
        if (this.pressedField != v) {
            markRenderDirty() // FR-30 B4：pressed 态变化影响视觉
        }
        this.pressedField = v
        return self()
    }

    open fun focused(v: Boolean): SELF {
        if (this.focusedField != v) {
            markRenderDirty() // FR-30 B4：focus 态变化影响视觉
        }
        this.focusedField = v
        return self()
    }

    fun hovered(): Boolean = hoveredField

    fun hoverSinceMs(): Long = hoverSinceMsField

    /** 测试与平台事件桥接可显式设置悬停起始时间；0 表示不启用延迟。 */
    fun hoverSinceMs(v: Long): SELF {
        this.hoverSinceMsField = v
        return self()
    }

    fun pressed(): Boolean = pressedField

    fun focused(): Boolean = focusedField

    /** 元素是否启用；disabled 时渲染层应应用对应的视觉样式（降暗 / 不可交互）。默认 true。 */
    open fun enabled(): Boolean = true

    /**
     * 当前节点是否可参与 hotkey 派发；默认仅要求 visible。
     * Button（jvmMain 留守）覆写为额外要求 enabled 且非 loading——原 companion 里的
     * `is Button` 特判下沉为可覆写钩子，避免 commonMain 反向引用留守子类。
     */
    open fun hotkeyEligible(): Boolean = visible()

    override fun onClickHandler(): UiEventHandler<MouseEvent>? = onClickField

    override fun onRightClickHandler(): UiEventHandler<MouseEvent>? = onRightClick

    override fun onHoverHandler(): UiEventHandler<MouseEvent>? = onHover

    override fun onLongPressHandler(): UiEventHandler<MouseEvent>? = onLongPress

    override fun onDragEnterHandler(): UiEventHandler<MouseEvent>? = onDragEnter

    override fun onDragLeaveHandler(): UiEventHandler<MouseEvent>? = onDragLeave

    override fun onChangeHandler(): UiEventHandler<MouseEvent>? = onChangeGeneric

    override fun onKeyHandler(): UiEventHandler<KeyEvent>? = onKeyField

    override fun onCharHandler(): UiEventHandler<CharEvent>? = onCharField

    override fun onDragHandler(): UiEventHandler<DragEvent>? = onDragField

    /** v4 C.2：获取拖拽结束 handler，未设置返回 `null`。 */
    override fun onDragEndHandler(): UiEventHandler<DragEvent>? = onDragEndField

    /** v4 C.2：获取指针移动 handler，未设置返回 `null`。 */
    override fun onPointerMoveHandler(): UiEventHandler<MouseEvent>? = onPointerMove

    override fun onScrollHandler(): UiEventHandler<ScrollEvent>? = onScrollField

    override fun onFocusGainedHandler(): UiEventHandler<FocusEvent>? = onFocusGained

    override fun onFocusLostHandler(): UiEventHandler<FocusEvent>? = onFocusLost

    override fun isFocusable(): Boolean = focusableField

    // -------- 生命周期 --------

    override fun lifecycleScope(): LifecycleScope = lifecycleScopeField

    // -------- 便利布局 setter（直接委托到 layout 字段） --------

    /**
     * 取布局样式用于写入：顺带标脏（FR-30 B1 push 式脏标记）。
     * 布局 setter 经此而非直接 [styleField].layout()，确保编程式修改触发增量重排；读取仍走 [layoutStyle] 不标脏。
     */
    private fun dirtyLayout(): LayoutStyle {
        markLayoutDirty()
        return styleField.layout()
    }

    fun width(d: Dimension): SELF {
        dirtyLayout().width = d
        return self()
    }

    fun height(d: Dimension): SELF {
        dirtyLayout().height = d
        return self()
    }

    fun padding(e: Edges): SELF {
        dirtyLayout().padding = e
        return self()
    }

    fun margin(v: Float): SELF = margin(Edges.all(v))

    fun margin(e: Edges): SELF {
        dirtyLayout().margin = e
        return self()
    }

    fun gap(g: Float): SELF {
        dirtyLayout().gap = g
        return self()
    }

    // -------- 绝对定位 setter（ABSOLUTE 下 x/y/z 生效；RELATIVE 时仅记录值不影响布局） --------

    fun positionMode(mode: PositionMode?): SELF {
        dirtyLayout().positionMode = mode ?: PositionMode.RELATIVE
        return self()
    }

    fun x(v: Dimension?): SELF {
        dirtyLayout().x = v ?: Dimension.auto()
        return self()
    }

    fun y(v: Dimension?): SELF {
        dirtyLayout().y = v ?: Dimension.auto()
        return self()
    }

    fun x(px: Int): SELF = x(Dimension.px(px.toFloat()))

    fun y(px: Int): SELF = y(Dimension.px(px.toFloat()))

    // -------- RFC-INPUT-EVENT-UNIFICATION Stage 3：通用 hotkey 绑定 --------

    /**
     * 设置 hotkey 字符串（如 `"Enter"` / `"Ctrl+S"` / `"MouseBack"`）。
     *
     * 大小写不敏感，匹配交由 [findHotkeyMatch] 处理。
     * 设为 `null` 或空串清除绑定。
     */
    fun hotkey(v: String?): SELF {
        this.hotkeyField = if (v.isNullOrEmpty()) null else v
        return self()
    }

    /** 获取 hotkey 字符串，可能为 null。 */
    fun hotkey(): String? = hotkeyField

    companion object {
        /** Tooltip 延迟计时未启动的标记值。 */
        const val HOVER_SINCE_MS_UNSET: Long = 0L

        /** 阶段 13：tabIndex 哨兵值；表示元素未声明 tabIndex，沿用 DOM 顺序参与 Tab 链。 */
        const val TAB_INDEX_UNSET: Int = Int.MIN_VALUE

        /**
         * v4 A.1：contextMenuTriggerMs 哨兵值 0L，表示元素未声明长按阈值；
         * 消费方应回退到主题 ControlTheme.longPressThresholdMs。
         */
        const val CONTEXT_MENU_TRIGGER_MS_UNSET: Long = 0L

        /** perspective 默认值：1000 像素视距，表示无明显透视效果。 */
        const val DEFAULT_PERSPECTIVE: Float = RenderTransform.DEFAULT_PERSPECTIVE

        /**
         * 深度优先遍历元素树，返回第一个 hotkey 匹配的元素：
         * 资格判定走 [hotkeyEligible]（默认跳过非 visible 元素；Button 覆写为额外要求 enabled 且非 loading）。
         *
         * @param root          元素树根节点
         * @param normalizedKey 已归一化的 hotkey 字符串（如 `"Ctrl+S"` / `"MouseBack"`），匹配时大小写不敏感
         * @return 匹配元素；root/key 为空或全树无匹配返回 `null`
         */
        @JvmStatic
        fun findHotkeyMatch(root: UIElement<*>?, normalizedKey: String?): UIElement<*>? {
            if (root == null || normalizedKey.isNullOrEmpty()) {
                return null
            }
            return findHotkeyMatchDfs(root, normalizedKey.lowercase())
        }

        /** 实际 DFS 实现：先匹配自身，再递归子节点；命中即返回。 */
        private fun findHotkeyMatchDfs(node: UIElement<*>?, lowerKey: String): UIElement<*>? {
            if (node == null) {
                return null
            }
            if (node.hotkeyEligible() && hotkeyMatches(node.hotkeyField, lowerKey)) {
                return node
            }
            for (child in node.children()) {
                val match = findHotkeyMatchDfs(child, lowerKey)
                if (match != null) {
                    return match
                }
            }
            return null
        }

        /** hotkey 字符串是否与已 lower 化的目标键匹配。 */
        private fun hotkeyMatches(hotkey: String?, lowerKey: String): Boolean =
            hotkey != null && hotkey.lowercase() == lowerKey
    }
}
